"use client"

import React, { useEffect, useState } from "react"
import { SEARCH_PARAMS_PAGE_VIEW_OPTIONS } from "@/constants/ui"
import { useTranslations } from "next-intl"

import type { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"
import type { ApplicationSettings } from "@/lib/settings/application-settings"
import type { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/lib/utils"
import FallbackCard from "@/components/common/FallbackCard"
import ProductCardWrapper from "@/components/product/ProductCardWrapper"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/components"

interface RecentlyViewedResponse {
  products?: ReducedProductData[]
}

export default function RecentlyViewedProductsClient(props: {
  title?: string
  templateBlok: any
  applicationSettings: ApplicationSettings
  sbLocale?: string
  isoLocale: string
}) {
  const t = useTranslations("storefront")
  const [products, setProducts] = useState<ReducedProductData[] | undefined>()
  const [error, setError] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    const searchParams = new URLSearchParams()
    if (props.sbLocale !== undefined) {
      searchParams.set("sbLocale", props.sbLocale)
    }

    fetch(`/api/storefront/recently-viewed?${searchParams.toString()}`, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await response.text())
        }
        return response.json() as Promise<RecentlyViewedResponse>
      })
      .then((data) => {
        if (!controller.signal.aborted) {
          setProducts(data.products ?? [])
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setError(true)
        }
      })

    return () => controller.abort()
  }, [props.sbLocale])

  if (error) {
    return null
  }

  if (products === undefined) {
    return <FallbackCard className="min-h-80 bg-gray-100" />
  }

  if (products.length === 0) {
    return null
  }

  const title = props.title ?? t("product.recently_viewed_products")
  const allowDebugMode = props.applicationSettings.allowDebugMode ?? false
  const hideAddToBasket = props.applicationSettings.hideAddToBasketButton ?? false

  return (
    <div className="flex w-full flex-col">
      <h2 className="text-2xl font-bold">{title}</h2>
      <Carousel
        className="mt-8 max-w-full px-0"
        opts={{
          align: "start",
          loop: true,
        }}
        duration={0}
        autoplay={false}
      >
        <CarouselContent>
          {products.map((productData, index) => (
            <CarouselItem key={productData.id} className="basis-full md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
              <ProductCardWrapper
                productData={productData}
                effectivePrices={productData.effectivePrices as ProductPriceContainerCtnrROV1 | undefined}
                view={SEARCH_PARAMS_PAGE_VIEW_OPTIONS[0]}
                cardVariant={props.templateBlok?.variant ?? "standard"}
                isB2B={true}
                showEcoTax={props.templateBlok?.ecoTax ?? props.templateBlok?.showEcoTax ?? false}
                debugMode={props.templateBlok?.debugMode && allowDebugMode}
                hideAddToBasket={hideAddToBasket}
                priceRequestLink={props.applicationSettings.priceRequestLink ?? ""}
                sbLocale={props.sbLocale}
                isoLocale={props.isoLocale}
                trackingList="recently-viewed"
                searchTerm="recently_viewed"
                position={index + 1}
                mainProductId={undefined}
                isRecommendation={props.templateBlok?.isRecommendation}
                inStoryBlock={false}
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious
          className={cn("border-2 border-primary bg-white p-0.5 text-primary")}
          bannermode={false}
          fullwidth={true}
        />

        <CarouselNext
          className="border-2 border-primary bg-white p-0.5 text-primary"
          bannermode={false}
          fullwidth={true}
        />
      </Carousel>
    </div>
  )
}
